import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../Context/TenantContext";
import { GraduationCap, Target, X, ArrowLeft } from "lucide-react";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import { hasFullAccess } from "../utils/paymentStatus";

const SkillgenLaunchpadLevels = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const { track, topic, fullTrackName } = location.state || {};
  const [unlockedLevel, setUnlockedLevel] = useState(1);
  const [userId, setUserId] = useState("guest");
  const [hasPaid, setHasPaid] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showModeModal, setShowModeModal] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    const id = storedUser?.userId || "guest";
    setUserId(id);

    const isFullTrack = topic === fullTrackName;
    const subtopic = isFullTrack ? fullTrackName : topic;

    const fetchProgress = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/sgl/passed-levels/${id}/${track}/${subtopic}`
        );

        const passedLevels = res.data?.passedLevels || [];
        const max = passedLevels.length ? Math.max(...passedLevels) : 0;
        setUnlockedLevel(max + 1);
      } catch (err) {
        console.error("⚠️ Failed to fetch passed levels:", err);
        setUnlockedLevel(1);
      }
    };

    fetchProgress();

    // ✅ LocalStorage override after immediate level completion
    const levelData = JSON.parse(localStorage.getItem("pendingLevelComplete"));
    const completed = localStorage.getItem("levelCompleted");

    if (levelData && completed === "true" && levelData.topic === topic) {
      const { completedLevel } = levelData;
      setUnlockedLevel((prev) => Math.max(prev, completedLevel + 1));
      localStorage.removeItem("levelCompleted");
      localStorage.removeItem("pendingLevelComplete");
    }
  }, [topic, fullTrackName, track]);

  useEffect(() => {
    if (!userId || userId === "guest") return;

    // ✅ Always grant access to student tenant or if tenant mode is active
    const user = JSON.parse(localStorage.getItem("user"));
    const isTenantStudent = user && user.role === "student" && user.tenant;

    if (isTenantStudent || tenant) {
      setHasPaid(true);
      setCheckingPayment(false);
      return;
    }

    // For regular learners, check if they've reached level 6
    if (unlockedLevel >= 6) {
      (async () => {
        try {
          setCheckingPayment(true);
          const userHasFullAccess = await hasFullAccess(userId);
          setHasPaid(userHasFullAccess);
        } catch (err) {
          console.error("⚠️ Failed to check payment status:", err);
          setHasPaid(false);
        } finally {
          setCheckingPayment(false);
        }
      })();
    } else {
      setHasPaid(false);
    }
  }, [userId, unlockedLevel, tenant]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleLevelClick = (level) => {
    // Check if it's a restricted level for free users
    if (level >= 6 && !hasPaid) {
      navigate("/skillgenpayments");
      return;
    }

    if (level > unlockedLevel) return;

    setSelectedLevel(level);
    setShowModeModal(true);
  };

  const startMode = (selectedMode) => {
    if (!selectedLevel) return;

    const user = JSON.parse(localStorage.getItem("user"));
    const isTenantStudent = user && user.role === "student" && user.tenant;
    const targetPath = isTenantStudent ? "/student/skillgen-questions" : "/skillgen-questions";

    // ✅ Clean up state and force a fresh navigation
    setShowModeModal(false);
    
    // Use setTimeout to ensure modal is closed before navigation
    setTimeout(() => {
      navigate(targetPath, {
        state: { 
          track, 
          topic, 
          level: selectedLevel, 
          fullTrackName, 
          mode: selectedMode,
          technology: track // Adding technology explicitly
        },
      });
    }, 10);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-body flex flex-col items-center p-4">
      {/* Branded Header */}
      <div className="w-full bg-white/80 backdrop-blur-md border-b border-purple-100 py-4 px-8 mb-8 flex justify-between items-center rounded-2xl shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#5B21B6] rounded-xl flex items-center justify-center text-white font-black shadow-lg shadow-purple-200">
            SG
          </div>
          <h1 className="text-xl font-black text-gray-900 tracking-tight">
            TheSkillGenie
          </h1>
        </div>
      </div>

      <div className="max-w-4xl w-full p-10 rounded-[3rem] bg-white shadow-xl shadow-purple-100 relative overflow-hidden flex flex-col items-center border border-purple-50">
        <button
          onClick={handleBack}
          className="absolute top-8 left-8 px-5 py-2.5 bg-[#5B21B6] text-white font-bold rounded-xl hover:bg-[#4C1D95] transition shadow-lg shadow-purple-200 text-sm flex items-center gap-2"
        >
          <ArrowLeft size={18} /> Back
        </button>

        {/* Background decoration */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-purple-50 rounded-full blur-3xl"></div>

        <h1 className="text-4xl font-black text-[#5B21B6] mb-12 uppercase tracking-tighter text-center">
          {topic}
        </h1>

        <div className="w-full space-y-16">
          {[
            { phase: "Phase 1", levels: [1, 2, 3, 4, 5], color: "primary" },
            {
              phase: "Phase 2",
              levels: [6, 7, 8, 9, 10, 11, 12, 13, 14],
              color: "primary",
            },
          ].map(({ phase, levels, color }) => (
            <div key={phase} className="w-full">
              <div className="text-center mb-8">
                <h2 className="text-[#5B21B6] text-2xl font-black uppercase tracking-[0.2em]">
                  {phase}
                </h2>
                <div className="h-1 w-12 bg-[#5B21B6] mx-auto mt-2 rounded-full opacity-30"></div>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                {levels.map((level) => {
                  const isUnlocked = level <= unlockedLevel;
                  const isPaywallLevel =
                    level >= 6 && !hasPaid;
                  const isClickable = isUnlocked || isPaywallLevel;

                  let buttonClasses = "";
                  if (isPaywallLevel) {
                    buttonClasses =
                      "bg-amber-500 text-white hover:scale-105 shadow-xl animate-pulse ring-4 ring-amber-500/20";
                  } else if (isUnlocked) {
                    buttonClasses =
                      "bg-[#5B21B6] text-white hover:scale-105 shadow-xl";
                  } else {
                    buttonClasses =
                      "bg-gray-100 text-gray-300 cursor-not-allowed border border-gray-200";
                  }

                  return (
                    <button
                      key={level}
                      onClick={() => handleLevelClick(level)}
                      className={`w-24 h-12 rounded-xl font-black text-xs uppercase tracking-widest transition-all duration-300 ${buttonClasses}`}
                      disabled={!isClickable}
                    >
                      {isPaywallLevel ? "🔒 Lvl " + level : "Level " + level}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {checkingPayment && (
        <div className="mt-8 text-[#5B21B6] text-[10px] uppercase font-black tracking-widest animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-[#5B21B6]/30 border-t-[#5B21B6] rounded-full animate-spin"></div>
          Verifying Access...
        </div>
      )}

      {/* Mode Selection Modal */}
      {showModeModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in duration-300 relative text-center">
            <button 
              onClick={() => setShowModeModal(false)}
              className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>

            <div className="p-10">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Level {selectedLevel}</h2>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Select Mode</p>
                <div className="h-1 w-12 bg-[#5B21B6] mx-auto mt-4 rounded-full"></div>
              </div>
              
              <div className="space-y-4">
                <button 
                  onClick={() => startMode('learning')}
                  className="w-full flex items-center gap-5 p-6 rounded-3xl border-2 border-gray-100 hover:border-[#5B21B6] hover:bg-[#5B21B6]/5 transition-all group"
                >
                  <div className="p-4 rounded-2xl bg-gray-50 text-gray-400 group-hover:bg-[#5B21B6] group-hover:text-white transition-all shadow-sm">
                    <GraduationCap size={28} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-lg text-gray-900">Learning Mode</h4>
                    <p className="text-xs text-gray-500 font-medium">Study with AI explanations</p>
                  </div>
                </button>

                <button 
                  onClick={() => startMode('practice')}
                  className="w-full flex items-center gap-5 p-6 rounded-3xl border-2 border-gray-100 hover:border-[#5B21B6] hover:bg-[#5B21B6]/5 transition-all group"
                >
                  <div className="p-4 rounded-2xl bg-gray-50 text-gray-400 group-hover:bg-[#5B21B6] group-hover:text-white transition-all shadow-sm">
                    <Target size={28} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-lg text-gray-900">Practice Mode</h4>
                    <p className="text-xs text-gray-500 font-medium">Test & Evaluation Mode</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillgenLaunchpadLevels;



